/**
 * 리뷰 콘텐츠 변환 관련 유틸리티 함수들
 */

/**
 * [7단계]부터 '추가검토내용' 전까지를 앞으로 이동시키는 함수
 * CMM_2 또는 LCMM 프롬프트 타입인 경우에만 적용
 */
export function reorderSectionsByPromptType(text: string, promptType?: string): string {
  if (!text || !promptType) return text;
  
  // CMM_2 또는 LCMM인 경우에만 재배치
  if (promptType !== "cmm_2" && promptType !== "lcmm") {
    return text;
  }
  
  // DRP 태그로 감싸진 내용 찾기
  const drpMatch = text.match(/\{DRP\}[\s\S]*?\{\/DRP\}/);
  let step7Match;
  let step7Start;
  
  if (drpMatch && drpMatch[0].includes('[7단계]')) {
    // DRP 태그 안에 7단계가 있는 경우
    step7Start = drpMatch.index;
  } else {
    // 일반적인 [7단계] 찾기
    step7Match = text.match(/^# \[7단계\]/m);
    if (!step7Match || !step7Match.index) {
      return text; // [7단계]가 없으면 원본 반환
    }
    step7Start = step7Match.index;
  }
  
  // [7단계] 섹션의 끝 위치 찾기
  let step7End = text.length; // 기본값: 문서 끝
  
  if (drpMatch && drpMatch[0].includes('[7단계]')) {
    // DRP 태그로 감싸진 경우, DRP 닫는 태그까지가 섹션의 끝
    step7End = step7Start! + drpMatch[0].length;
  } else {
    const textAfterStep7 = text.substring(step7Start!);

    // 경계 후보 수집 (가장 빠른 위치를 step7 종료 지점으로 사용)
    const candidates: number[] = [];

    // 1순위: '추가검토내용' 찾기
    const additionalReviewMatch = textAfterStep7.match(/^# 추가검토내용/m);
    if (additionalReviewMatch?.index) {
      candidates.push(additionalReviewMatch.index);
    }

    // 2순위: 섹션 마커 ([환자용 복약지도문], [약사용 보고서], [종사자용 안내문]) - 절대 넘지 않는 경계
    const sectionMarkerMatch = textAfterStep7.match(/\[약사용 보고서\]|\[환자용 복약지도문?\]|\[종사자용 안내문\]/);
    if (sectionMarkerMatch?.index !== undefined) {
      // 섹션 마커 바로 앞의 --- (수평선)도 경계에 포함
      const textBeforeMarker = textAfterStep7.substring(0, sectionMarkerMatch.index);
      const hrMatch = textBeforeMarker.match(/\n---[ \t]*\n?\s*$/);
      candidates.push(hrMatch?.index !== undefined ? hrMatch.index : sectionMarkerMatch.index);
    }

    // 3순위: 다음 # 제목 찾기
    const nextHeaderMatch = textAfterStep7.match(/\n# /);
    if (nextHeaderMatch?.index) {
      candidates.push(nextHeaderMatch.index);
    }

    // 가장 빠른 경계를 step7 종료 지점으로 사용
    if (candidates.length > 0) {
      step7End = step7Start! + Math.min(...candidates);
    }
    // 후보 없으면 기본값(문서 끝)
  }
  
  // 텍스트 분할
  const beforeStep7 = text.substring(0, step7Start!);
  const step7Section = text.substring(step7Start!, step7End);
  const afterStep7 = text.substring(step7End);
  
  // [7단계]를 <badge>분석결과 요약</badge>으로 변경
  let modifiedStep7Section: string;
  if (drpMatch && step7Section.includes('{DRP}')) {
    // DRP 태그가 있는 경우, 태그 내부의 제목만 변경
    modifiedStep7Section = step7Section.replace(
      /(\{DRP\}[\s\S]*?)^#\s*\[7단계\][^\n]*$/gm,
      '$1<badge>분석결과 요약</badge>'
    );
    
    // DRP 태그 내부의 11, 12, 13, 14 넘버링을 1, 2, 3, 4로 변경
    modifiedStep7Section = modifiedStep7Section.replace(/\{DRP\}([\s\S]*?)\{\/DRP\}/g, (match, content) => {
      // 넘버링 매핑 (11->1, 12->2, 13->3, 14->4)
      let modifiedContent = content;
      
      // ## 형식의 헤더 처리
      modifiedContent = modifiedContent.replace(/^(\s*)##\s*11\.\s/gm, '$1## 1. ');
      modifiedContent = modifiedContent.replace(/^(\s*)##\s*12\.\s/gm, '$1## 2. ');
      modifiedContent = modifiedContent.replace(/^(\s*)##\s*13\.\s/gm, '$1## 3. ');
      modifiedContent = modifiedContent.replace(/^(\s*)##\s*14\.\s/gm, '$1## 4. ');
      
      // 일반 리스트 형식 처리
      modifiedContent = modifiedContent.replace(/^(\s*)11\.\s/gm, '$11. ');
      modifiedContent = modifiedContent.replace(/^(\s*)12\.\s/gm, '$12. ');
      modifiedContent = modifiedContent.replace(/^(\s*)13\.\s/gm, '$13. ');
      modifiedContent = modifiedContent.replace(/^(\s*)14\.\s/gm, '$14. ');
      
      return `{DRP}${modifiedContent}{/DRP}`;
    });
  } else {
    // 일반적인 경우 (DRP 태그 없음)
    modifiedStep7Section = step7Section.replace(
      /^#\s*\[7단계\][^\n]*$/gm,
      '<badge>분석결과 요약</badge>'
    );

    // 넘버링 변환 (11->1, 12->2, 13->3, 14->4) - DRP 없이도 적용
    modifiedStep7Section = modifiedStep7Section.replace(/^(\s*)##\s*11\.\s/gm, '$1## 1. ');
    modifiedStep7Section = modifiedStep7Section.replace(/^(\s*)##\s*12\.\s/gm, '$1## 2. ');
    modifiedStep7Section = modifiedStep7Section.replace(/^(\s*)##\s*13\.\s/gm, '$1## 3. ');
    modifiedStep7Section = modifiedStep7Section.replace(/^(\s*)##\s*14\.\s/gm, '$1## 4. ');
    modifiedStep7Section = modifiedStep7Section.replace(/^(\s*)11\.\s/gm, '$11. ');
    modifiedStep7Section = modifiedStep7Section.replace(/^(\s*)12\.\s/gm, '$12. ');
    modifiedStep7Section = modifiedStep7Section.replace(/^(\s*)13\.\s/gm, '$13. ');
    modifiedStep7Section = modifiedStep7Section.replace(/^(\s*)14\.\s/gm, '$14. ');
  }
  
  // [7단계] 섹션을 문서 맨 앞으로 이동
  // 상세 검토 섹션 마커 추가
  const detailSectionMarker = "{DETAIL_SECTION_START}\n";
  const detailSectionEnd = "\n{DETAIL_SECTION_END}";

  return modifiedStep7Section + "\n\n" +
         detailSectionMarker + beforeStep7 + afterStep7 + detailSectionEnd;
} 